import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Plus, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/lib/supabase";
import { glassCardClass, glassMutedCardClass } from "@/recruitment/styles.js";

const MAIN_APP_LOGIN_URL = import.meta.env.VITE_MAIN_APP_LOGIN_URL || "https://kconnect.pl/login";

const statusLabels = {
  draft: "Szkic",
  scheduled: "Zaplanowana",
  intake_open: "Nabór otwarty",
  intake_closed: "Nabór zamknięty",
  published: "Opublikowana",
  archived: "Archiwum",
};

const pendingEligibleStatuses = new Set(["draft", "scheduled", "intake_open", "intake_closed"]);

export default function AdminCampaignsPage() {
  const [profile, setProfile] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [pendingCounts, setPendingCounts] = useState({});

  const canManageRecruitment = useMemo(() => {
    if (!profile) return false;
    const roles = profile.roles || [];
    return roles.includes("branch_admin") || roles.includes("super_admin");
  }, [profile]);

  const getSessionFromHash = () => {
    const rawHash = window.location.hash || "";
    if (!rawHash.startsWith("#")) return null;
    const params = new URLSearchParams(rawHash.slice(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    if (!accessToken || !refreshToken) return null;
    return { access_token: accessToken, refresh_token: refreshToken };
  };

  const clearHash = () => {
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanUrl);
  };

  const redirectToMainLogin = () => {
    const targetUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    try {
      const loginUrl = new URL(MAIN_APP_LOGIN_URL, window.location.origin);
      loginUrl.searchParams.set("next", targetUrl);
      window.location.href = loginUrl.toString();
      return;
    } catch (_) {
      const fallback = new URL("https://kconnect.pl/login");
      fallback.searchParams.set("next", targetUrl);
      window.location.href = fallback.toString();
    }
  };

  const refreshCampaigns = async (branchId) => {
    if (!branchId) {
      setCampaigns([]);
      setPendingCounts({});
      return;
    }

    const { data, error } = await supabase
      .from("recruitment_campaigns")
      .select("id, campaign_name, public_slug, status, intake_start_at, intake_end_at, decision_publish_at, capacity, branch_id, created_at, settings")
      .eq("branch_id", branchId)
      .order("created_at", { ascending: false })
      .limit(200);

    if (error) {
      toast.error(error.message || "Nie udało się pobrać kampanii.");
      return;
    }

    const campaignRows = data ?? [];
    setCampaigns(campaignRows);

    if (campaignRows.length === 0) {
      setPendingCounts({});
      return;
    }

    const campaignIds = campaignRows.map((row) => row.id);
    const { data: pendingRows, error: pendingError } = await supabase
      .from("recruitment_applications")
      .select("campaign_id, status")
      .in("campaign_id", campaignIds)
      .in("status", ["submitted", "under_verification"]);

    if (pendingError) {
      toast.error(pendingError.message || "Nie udało się pobrać liczby oczekujących wniosków.");
      return;
    }

    const nextCounts = {};
    for (const row of pendingRows ?? []) {
      nextCounts[row.campaign_id] = (nextCounts[row.campaign_id] || 0) + 1;
    }
    setPendingCounts(nextCounts);
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        let user = null;

        const {
          data: { user: existingUser },
          error: authError,
        } = await supabase.auth.getUser();
        if (authError && !String(authError.message || "").toLowerCase().includes("auth session missing")) {
          throw authError;
        }
        user = existingUser;

        if (!user) {
          const hashSession = getSessionFromHash();
          if (hashSession) {
            const { error: setSessionError } = await supabase.auth.setSession(hashSession);
            if (setSessionError) throw setSessionError;

            clearHash();
            const {
              data: { user: userAfterSessionSet },
              error: refreshUserError,
            } = await supabase.auth.getUser();
            if (refreshUserError) throw refreshUserError;
            user = userAfterSessionSet;
          }
        }

        if (!user) {
          redirectToMainLogin();
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("app_users")
          .select("id, roles, branch_ids")
          .eq("id", user.id)
          .maybeSingle();
        if (profileError) throw profileError;
        setProfile(profileData ?? null);

        const currentBranchId = profileData?.branch_ids?.[0] ?? "";
        if (currentBranchId) {
          const { data: branchData, error: branchError } = await supabase
            .from("branches")
            .select("id, name")
            .eq("id", currentBranchId)
            .maybeSingle();

          if (branchError) throw branchError;
          setBranch(branchData ?? null);
        }

        await refreshCampaigns(currentBranchId);
      } catch (err) {
        const message = String(err?.message || "");
        if (message.toLowerCase().includes("auth session missing")) {
          redirectToMainLogin();
          return;
        }
        toast.error(message || "Nie udało się załadować panelu administratora.");
        redirectToMainLogin();
      } finally {
        setLoadingProfile(false);
      }
    };

    bootstrap();
  }, []);

  if (loadingProfile) {
    return <p className="text-sm text-slate-500">Ładowanie panelu administratora...</p>;
  }

  if (!canManageRecruitment) {
    return (
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Brak dostępu</CardTitle>
          <CardDescription>Ten panel jest dostępny dla ról `branch_admin` i `super_admin`.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (!search.trim()) return true;
    const normalized = search.trim().toLowerCase();
    const schoolYear = campaign?.settings?.schoolYear || "";
    return (
      campaign.campaign_name?.toLowerCase().includes(normalized) ||
      campaign.public_slug?.toLowerCase().includes(normalized) ||
      schoolYear.toLowerCase().includes(normalized)
    );
  });

  const displayName = profile?.id || "Administrator";
  const branchDisplayName = branch?.name || "Brak przypisanej placówki";

  return (
    <div className="space-y-6">
      <Card className={glassCardClass}>
        <CardHeader>
          <CardTitle>Panel administratora</CardTitle>
          <CardDescription>
            Zalogowany: <span className="font-medium text-slate-900">{displayName}</span>
          </CardDescription>
          <CardDescription>
            Placówka: <span className="font-medium text-slate-900">{branchDisplayName}</span>
          </CardDescription>
        </CardHeader>
      </Card>

      <Card className={glassCardClass}>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Kampanie w placówce</CardTitle>
            <CardDescription>Aktualna placówka: {branchDisplayName}</CardDescription>
          </div>
          <Button asChild className="inline-flex items-center gap-2">
            <Link to="/admin/campaign/new">
              <Plus className="h-4 w-4" />
              Nowa kampania
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-10"
              placeholder="Szukaj po nazwie kampanii, roku szkolnym lub slug..."
            />
          </div>

          {filteredCampaigns.length === 0 ? (
            <p className="text-sm text-slate-500">Brak kampanii dla tej placówki.</p>
          ) : (
            filteredCampaigns.map((campaign) => {
              const schoolYear = campaign?.settings?.schoolYear || "Nie ustawiono";
              const pendingCount = pendingCounts[campaign.id] || 0;
              const showPending = pendingEligibleStatuses.has(campaign.status);

              return (
                <div key={campaign.id} className={`flex flex-col gap-3 rounded-lg p-4 md:flex-row md:items-center md:justify-between ${glassMutedCardClass}`}>
                  <div className="space-y-1">
                    <p className="font-semibold">{campaign.campaign_name}</p>
                    <p className="text-sm text-slate-600">Rok szkolny: {schoolYear}</p>
                    <p className="text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {new Date(campaign.intake_start_at).toLocaleDateString()}
                        {" -> "}
                        {new Date(campaign.intake_end_at).toLocaleDateString()}
                        {" -> "}
                        {new Date(campaign.decision_publish_at).toLocaleDateString()}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">status: {statusLabels[campaign.status] || campaign.status}</Badge>
                    <Badge variant="outline">miejsca: {campaign.capacity}</Badge>
                    {showPending ? <Badge>oczekujące: {pendingCount}</Badge> : null}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="inline-flex items-center gap-2">
                          <Settings2 className="h-4 w-4" />
                          Akcje
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/campaign/${campaign.id}`}>Otwórz kampanię</Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
